import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { formatAuDate } from "@/lib/date";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowLeft, Loader2, Plus, Trash2, ListPlus, FileText, ExternalLink, Info, Lock, Share2, Mail, MessageCircle, Check } from "lucide-react";


const sessionName = (s) => (typeof s === "string" ? s : s.name);

export default function Register() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", mobile: "" });
  const [profile, setProfile] = useState({
    date_of_birth: "",
    shirt_size: "",
    location: "",
    dietary: "",
    medical_notes: "",
    organised_group: "",
    event_experience: "",
    ec1_name: "", ec1_mobile: "",
    ec2_name: "", ec2_mobile: "",
  });
  const [current, setCurrent] = useState({ day: "", session: "", role: "", unlock_code: "", unallocated: false });
  const [itinerary, setItinerary] = useState([]);
  const [captcha, setCaptcha] = useState({ id: "", question: "", answer: "" });
  const [honeypot, setHoneypot] = useState("");
  const [disclaimerAck, setDisclaimerAck] = useState(false);
  const [disclaimerOpened, setDisclaimerOpened] = useState(false);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [unlockCheckState, setUnlockCheckState] = useState("idle"); // idle | checking | ok | bad
  const navigate = useNavigate();

  async function loadCaptcha() {
    try {
      const { data } = await api.get("/captcha/challenge");
      setCaptcha({ id: data.id, question: data.question, answer: "" });
    } catch { /* silent */ }
  }

  useEffect(() => {
    api.get(`/public/events/${slug}`).then((r) => setEv(r.data)).catch(() => setNotFound(true));
    loadCaptcha();
  }, [slug]);

  const daysMap = useMemo(() => {
    const m = {};
    (ev?.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [ev]);

  const selectedDay = current.day ? daysMap[current.day] : null;
  const daySessions = selectedDay?.sessions || [];
  const selectedSession = selectedDay && current.session
    ? daySessions.find((s) => sessionName(s) === current.session)
    : null;
  const sessionRoles = selectedSession?.roles || [];
  const sessionRoleDescriptions = selectedSession?.role_descriptions || {};
  const selectedRoleDescription = current.role ? (sessionRoleDescriptions[current.role] || "") : "";
  const lockedRoles = selectedSession?.locked_roles || [];
  const isLockedRoleSelected = !!current.role && lockedRoles.includes(current.role);
  const codeValid = /^\d{3}$/.test((current.unlock_code || "").trim());
  const codeVerified = isLockedRoleSelected ? unlockCheckState === "ok" : true;

  // Auto-check unlock code as soon as 3 digits are entered
  useEffect(() => {
    if (!isLockedRoleSelected) { setUnlockCheckState("idle"); return; }
    if (!codeValid) { setUnlockCheckState("idle"); return; }
    let cancelled = false;
    setUnlockCheckState("checking");
    (async () => {
      try {
        const { data } = await api.post(`/public/events/${slug}/check-unlock`, {
          day: current.day, session: current.session, role: current.role,
          code: current.unlock_code.trim(),
        });
        if (cancelled) return;
        setUnlockCheckState(data.ok ? "ok" : "bad");
      } catch {
        if (!cancelled) setUnlockCheckState("bad");
      }
    })();
    return () => { cancelled = true; };
  }, [current.unlock_code, current.role, current.session, current.day, isLockedRoleSelected, codeValid, slug]);

  const canAddSlot = current.unallocated
    ? !!current.day && !itinerary.some((s) => s.unallocated && s.day === current.day)
    : (current.day && current.session && current.role && (!isLockedRoleSelected || codeVerified));
  const alreadyAdded = (slot) => itinerary.some((s) => s.day === slot.day && s.session === slot.session && s.role === slot.role && s.unallocated === !!slot.unallocated);

  // ---------------- Time-conflict detection helpers ----------------
  const [conflictState, setConflictState] = useState(null); // { newSlot, existing: [slot,...] } | null
  function sessionTimes(dayIso, sessionName) {
    const d = (ev?.days || []).find((x) => x.date === dayIso);
    if (!d) return { start: "", end: "" };
    const s = (d.sessions || []).find((x) => (typeof x === "string" ? x : x.name) === sessionName);
    if (!s || typeof s === "string") return { start: "", end: "" };
    return { start: s.start || "", end: s.end || "" };
  }
  function toMin(hhmm) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
    if (!m) return NaN;
    return Number(m[1]) * 60 + Number(m[2]);
  }
  function timesOverlap(a, b) {
    const a1 = toMin(a.start), a2 = toMin(a.end);
    const b1 = toMin(b.start), b2 = toMin(b.end);
    if ([a1, a2, b1, b2].some((n) => isNaN(n))) return false;
    return a1 < b2 && b1 < a2; // strict overlap; touching (end == start) is OK
  }
  function findConflicts(newSlot) {
    if (newSlot.unallocated) return [];
    const nt = sessionTimes(newSlot.day, newSlot.session);
    if (!nt.start || !nt.end) return []; // if no times set on the session we can't detect a clash
    return itinerary.filter((s) => {
      if (s.unallocated) return false;
      if (s.day !== newSlot.day) return false;
      if (s.session === newSlot.session && s.role === newSlot.role) return false;
      const et = sessionTimes(s.day, s.session);
      return timesOverlap(nt, et);
    });
  }

  function addSlot() {
    if (!canAddSlot) return;
    let slot;
    if (current.unallocated) {
      slot = { day: current.day, session: "", role: "", unallocated: true };
    } else {
      slot = { day: current.day, session: current.session, role: current.role, unallocated: false };
      if (isLockedRoleSelected) slot.unlock_code = (current.unlock_code || "").trim();
    }
    if (alreadyAdded(slot)) return toast.error(current.unallocated ? "You've already asked us to pick a role on that day" : "You've already added this slot");

    // Time-overlap detection — the new slot runs at the same time as another placed slot
    const clashes = findConflicts(slot);
    if (clashes.length > 0) {
      setConflictState({ newSlot: slot, existing: clashes });
      return; // wait for modal decision
    }

    setItinerary((it) => [...it, slot]);
    setCurrent({ day: "", session: "", role: "", unlock_code: "", unallocated: false });
    setUnlockCheckState("idle");
    toast.success(slot.unallocated ? "Added — organiser will pick a role for you" : "Added to your itinerary");
  }

  function resolveConflictKeepAll() {
    if (!conflictState) return;
    const { newSlot, existing } = conflictState;
    const day = newSlot.day;
    // Preferences = every conflicting slot (existing + new). De-dupe by session+role.
    const seen = new Set();
    const preferences = [newSlot, ...existing].reduce((acc, s) => {
      const k = `${s.session}||${s.role}`;
      if (!seen.has(k) && s.session && s.role) { seen.add(k); acc.push({ session: s.session, role: s.role }); }
      return acc;
    }, []);
    setItinerary((it) => {
      // Remove all conflicting existing slots on this day + any pre-existing unallocated slot on this day
      let filtered = it.filter((s) => !(s.day === day && (existing.includes(s) || s.unallocated)));
      // Merge any preferences from a pre-existing unallocated slot on this day
      const priorUnalloc = it.find((s) => s.day === day && s.unallocated);
      const priorPrefs = (priorUnalloc?.preferences || []);
      const mergedSeen = new Set(preferences.map((p) => `${p.session}||${p.role}`));
      priorPrefs.forEach((p) => {
        const k = `${p.session}||${p.role}`;
        if (!mergedSeen.has(k)) { mergedSeen.add(k); preferences.push(p); }
      });
      filtered.push({ day, session: "", role: "", unallocated: true, preferences });
      return filtered;
    });
    setConflictState(null);
    setCurrent({ day: "", session: "", role: "", unlock_code: "", unallocated: false });
    setUnlockCheckState("idle");
    toast.success(`Time conflict noted — organiser will choose from your ${preferences.length} preferred role${preferences.length !== 1 ? "s" : ""}.`);
  }
  function resolveConflictDrop() {
    setConflictState(null);
    setUnlockCheckState("idle");
    toast.info("Selection removed. Pick a different role or day.");
  }
  function removeSlot(i) { setItinerary((it) => it.filter((_, idx) => idx !== i)); }

  const disclaimerRequired = !!ev?.disclaimer;
  const fullName = `${contact.first_name.trim()} ${contact.last_name.trim()}`.trim();
  const signatureMatches = signature.trim().toLowerCase() === fullName.toLowerCase() && !!fullName;

  // Australian mobile validation — accepts common formats and normalises to 04XXXXXXXX (10 digits).
  function normaliseAuMobile(v) {
    const digits = String(v || "").replace(/[\s\-()+]/g, "");
    if (!digits) return "";
    if (digits.startsWith("614") && digits.length === 11) return "0" + digits.slice(2);
    if (digits.startsWith("61") && digits.length === 11) return "0" + digits.slice(2);
    if (digits.startsWith("4") && digits.length === 9) return "0" + digits;
    return digits;
  }
  function isValidAuMobile(v) {
    return /^04\d{8}$/.test(normaliseAuMobile(v));
  }
  const ec1MobileValid = isValidAuMobile(profile.ec1_mobile);
  const ec2Provided = !!(profile.ec2_name.trim() || profile.ec2_mobile.trim());
  const ec2MobileValid = !ec2Provided || isValidAuMobile(profile.ec2_mobile);

  // MANDATORY: email, mobile, EC1 (name + valid AU mobile). Also disclaimer must be OPENED before ack allowed.
  const ecValid = !!profile.ec1_name.trim() && ec1MobileValid && ec2MobileValid;
  const step1Done = !!(contact.first_name.trim() && contact.last_name.trim() && contact.email && contact.mobile && profile.location && ecValid);
  const canSubmit = contact.first_name.trim() && contact.last_name.trim() && contact.email && contact.mobile && profile.location
    && itinerary.length > 0 && captcha.answer.trim() && ecValid
    && (!disclaimerRequired || (disclaimerOpened && disclaimerAck && signatureMatches));

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        name: fullName,
        first_name: contact.first_name.trim(),
        last_name: contact.last_name.trim(),
        email: contact.email, mobile: contact.mobile,
        slots: itinerary,
        captcha_id: captcha.id, captcha_answer: captcha.answer, hp_website: honeypot,
        disclaimer_ack: disclaimerAck,
        signature: disclaimerRequired ? signature.trim() : "",
        date_of_birth: profile.date_of_birth || "",
        shirt_size: profile.shirt_size || "",
        location: profile.location,
        dietary: profile.dietary || "",
        medical_notes: profile.medical_notes || "",
        organised_group: profile.organised_group || "",
        event_experience: profile.event_experience || "",
        emergency_contact_1: { name: profile.ec1_name || "", mobile: profile.ec1_mobile || "" },
        emergency_contact_2: { name: profile.ec2_name || "", mobile: profile.ec2_mobile || "" },
      };
      const { data } = await api.post(`/public/events/${slug}/register-itinerary`, payload);
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      loadCaptcha();
    } finally { setSubmitting(false); }
  }

  function reset() {
    setResult(null); setItinerary([]); setContact({ first_name: "", last_name: "", email: "", mobile: "" });
    setProfile({ date_of_birth: "", location: "", shirt_size: "", dietary: "", medical_notes: "", organised_group: "", event_experience: "", ec1_name: "", ec1_mobile: "", ec2_name: "", ec2_mobile: "" });
    setCurrent({ day: "", session: "", role: "", unlock_code: "", unallocated: false }); setDisclaimerAck(false); setDisclaimerOpened(false); setSignature(""); loadCaptcha();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function acceptSuggestion(sug) {
    if (!sug) return;
    setResult(null);
    setItinerary([sug]);
    setCurrent({ day: "", session: "", role: "", unlock_code: "", unallocated: false });
    loadCaptcha();
    toast.info("Suggested slot loaded — submit again to book it");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center border border-black/10 p-10">
          <h1 className="font-display text-2xl font-black">Event not available</h1>
          <p className="mt-2 text-sm text-zinc-600">Registration is not open for this event.</p>
          <Link to="/" className="mt-6 inline-block brand-btn px-4 py-2 text-sm">VOLMEE Home</Link>
        </div>
      </div>
    );
  }
  if (!ev) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>;
  }

  if (result) {
    return <ResultScreen result={result} slug={slug} ev={ev} onReset={reset} onAcceptSuggestion={acceptSuggestion} navigate={navigate} />;
  }

  const disclaimerUrl = ev.disclaimer ? `${BACKEND_URL}/api/public/events/${slug}/disclaimer` : null;
  const promoUrl = ev.promo_image ? `${BACKEND_URL}/api/public/events/${slug}/promo-image` : null;

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {conflictState ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="role-conflict-modal">
          <div className="w-full max-w-lg border-2 border-[#FF3B30] bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center bg-[#FF3B30] text-white">!</div>
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-[#FF3B30]">Role conflict</div>
                <h3 className="mt-1 font-display text-2xl font-black tracking-tighter">You have selected roles that run at the same time.</h3>
              </div>
            </div>
            <div className="mt-4 border border-black/10 bg-[#F9F9FB] p-3 text-xs">
              <div className="font-semibold text-zinc-700">On {formatAuDate(conflictState.newSlot.day)} these overlap:</div>
              <ul className="mt-2 space-y-1 font-mono text-[11px]">
                {[conflictState.newSlot, ...conflictState.existing].map((s, i) => {
                  const t = sessionTimes(s.day, s.session);
                  return (
                    <li key={`${s.session}-${s.role}-${i}`} className="flex items-baseline gap-2" data-testid={`conflict-slot-${i}`}>
                      <span className="text-[#002FA7]">•</span>
                      <span className="font-semibold">{s.session}</span>
                      {(t.start || t.end) ? <span className="text-zinc-500">{t.start || "?"}–{t.end || "?"}</span> : null}
                      <span className="text-black">· {s.role}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <p className="mt-3 text-[11px] text-zinc-600">You can either keep all these preferences and let the organiser pick one for you (you'll appear on their chase list), or drop the role you just tried to add.</p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={resolveConflictKeepAll}
                data-testid="conflict-keep-btn"
                className="brand-btn inline-flex items-center justify-center px-4 py-3 text-xs font-semibold uppercase tracking-widest"
              >
                Keep selection &amp; let organiser choose
              </button>
              <button
                type="button"
                onClick={resolveConflictDrop}
                data-testid="conflict-drop-btn"
                className="inline-flex items-center justify-center border border-black bg-white px-4 py-3 text-xs font-semibold uppercase tracking-widest text-black hover:bg-black hover:text-white"
              >
                Remove this selection
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link to={`/events/${slug}`} className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black" data-testid="back-event-link">
          <ArrowLeft className="h-4 w-4" /> Back to event
        </Link>

        {promoUrl ? (
          <div className="mt-6 overflow-hidden border border-black/10 bg-white" data-testid="promo-image-banner">
            <img src={promoUrl} alt={ev.name} className="block max-h-[420px] w-full object-cover" />
          </div>
        ) : null}

        <div className="mt-6 border border-black/10 bg-white p-8 md:p-12 shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Volunteer signup for</div>
            <h2 className="mt-1 font-display text-3xl font-black tracking-tighter text-black md:text-4xl" data-testid="register-event-name">{ev.name}</h2>
            <h1 className="mt-4 font-display text-2xl font-black tracking-tighter text-zinc-500 md:text-3xl">Register in 3 easy steps.</h1>
            {ev.contact_email || ev.contact_phone ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border border-black/10 bg-[#F9F9FB] px-3 py-2 text-xs" data-testid="event-contact-block">
                <span className="font-medium uppercase tracking-widest text-zinc-500">Questions?</span>
                {ev.contact_email ? (
                  <a href={`mailto:${ev.contact_email}`} data-testid="event-contact-email" className="font-mono text-black underline">{ev.contact_email}</a>
                ) : null}
                {ev.contact_phone ? (
                  <>
                    {ev.contact_email ? <span className="text-zinc-400">·</span> : null}
                    <a href={`tel:${ev.contact_phone.replace(/\s+/g, "")}`} data-testid="event-contact-phone" className="font-mono text-black underline">📞 {ev.contact_phone}</a>
                  </>
                ) : null}
              </div>
            ) : null}
            <p className="mt-3 text-sm text-zinc-600">Pick every day / event element / role you want to volunteer for. Add as many as you like.</p>
          </div>

          <StepProgress steps={[
            { n: 1, label: "Your details", done: step1Done },
            { n: 2, label: "Build your itinerary", done: itinerary.length > 0 },
            { n: 3, label: disclaimerUrl ? "Disclaimer & submit" : "Check & submit", done: !!canSubmit },
          ]} />

          <StepHeader n={1} id="step-1" title="Your details" sub="The basic information we need to contact you and keep you safe on the day." done={step1Done} />

          {/* Contact */}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="First name *">
              <input required value={contact.first_name} onChange={(e) => setContact((c) => ({ ...c, first_name: e.target.value }))} className="input" placeholder="Jane" data-testid="input-first-name" />
            </Field>
            <Field label="Last name *">
              <input required value={contact.last_name} onChange={(e) => setContact((c) => ({ ...c, last_name: e.target.value }))} className="input" placeholder="Doe" data-testid="input-last-name" />
            </Field>
            <Field label="Mobile phone *">
              <input required value={contact.mobile} onChange={(e) => setContact((c) => ({ ...c, mobile: e.target.value }))} className="input" placeholder="+61 400 000 000" data-testid="input-mobile" />
            </Field>
            <Field label="Email address *">
              <input required type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} className="input" placeholder="you@example.com" data-testid="input-email" />
            </Field>
          </div>
          <div className="mt-4" data-testid="location-field">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-widest text-zinc-600">Location *</div>
            <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Location">
              {["Adelaide", "Regional SA", "Interstate"].map((loc) => (
                <button key={loc} type="button" role="radio" aria-checked={profile.location === loc}
                  onClick={() => setProfile((p) => ({ ...p, location: loc }))}
                  data-testid={`location-option-${loc.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`border px-4 py-3 text-sm font-semibold transition-colors ${profile.location === loc ? "border-black bg-black text-white" : "border-black/20 bg-white hover:border-black"}`}>
                  {loc}
                </button>
              ))}
            </div>
          </div>

          {/* About you */}
          <div className="mt-8 border border-black/10 bg-[#F9F9FB] p-5" data-testid="volunteer-profile-section">
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">About you</div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Field label="Date of birth (DD/MM/YYYY)">
                <input
                  type="date"
                  value={profile.date_of_birth}
                  max={new Date().toISOString().slice(0, 10)}
                  min="1900-01-01"
                  onChange={(e) => setProfile((p) => ({ ...p, date_of_birth: e.target.value }))}
                  className="input"
                  data-testid="input-dob"
                />
                {profile.date_of_birth ? (
                  <div className="mt-1 text-[11px] text-zinc-500" data-testid="dob-display">
                    {(() => {
                      const [y, m, d] = profile.date_of_birth.split("-");
                      return y && m && d ? `${d}/${m}/${y}` : "";
                    })()}
                  </div>
                ) : null}
              </Field>
              <Field label="Shirt size">
                <select value={profile.shirt_size} onChange={(e) => setProfile((p) => ({ ...p, shirt_size: e.target.value }))} className="input" data-testid="select-shirt-size">
                  <option value="">Select a size…</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                  <option value="XXXL">XXXL</option>
                </select>
              </Field>
              <Field label="Dietary requirements">
                <select value={profile.dietary} onChange={(e) => setProfile((p) => ({ ...p, dietary: e.target.value }))} className="input" data-testid="select-dietary">
                  <option value="">No specific requirements</option>
                  <option value="Coeliac">Coeliac</option>
                  <option value="Vegetarian">Vegetarian</option>
                  <option value="Nut allergy">Nut allergy</option>
                  <option value="Other">Other</option>
                </select>
              </Field>
              {ev.organised_groups && ev.organised_groups.length > 0 ? (
                <Field label="Organised group (optional)">
                  <select value={profile.organised_group} onChange={(e) => setProfile((p) => ({ ...p, organised_group: e.target.value }))} className="input" data-testid="select-organised-group">
                    <option value="">Not part of an organised group</option>
                    {ev.organised_groups.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
              ) : null}
            </div>
            <div className="mt-4">
              <Field label="Medical conditions / medications taken on-event (optional)">
                <textarea value={profile.medical_notes} onChange={(e) => setProfile((p) => ({ ...p, medical_notes: e.target.value.slice(0, 1000) }))} className="input min-h-[80px]" placeholder="Do you have any medical conditions or take necessary medications on-event? Please supply details. This is not mandatory." data-testid="input-medical-notes" maxLength={1000} />
                <div className="mt-1 text-right text-[10px] text-zinc-400">{profile.medical_notes.length}/1000</div>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Event experience (optional) — have you volunteered at events like this before?">
                <textarea value={profile.event_experience} onChange={(e) => setProfile((p) => ({ ...p, event_experience: e.target.value.slice(0, 1000) }))} className="input min-h-[70px]" placeholder="E.g. 3 years as Course Marshal at Targa Tas; First Aid Level 2; comfortable with radios; happy to work in adverse weather." data-testid="input-event-experience" maxLength={1000} />
                <div className="mt-1 text-right text-[10px] text-zinc-400">{profile.event_experience.length}/1000</div>
              </Field>
            </div>

            <div className="mt-6 border-t border-black/10 pt-4">
              <div className="text-xs uppercase tracking-widest text-[#002FA7]">Emergency contacts <span className="ml-1 rounded-sm bg-[#FF3B30] px-1.5 py-0.5 text-[9px] text-white">required</span></div>
              <p className="text-xs text-zinc-500">Give us at least one person we can reach if there's an incident on-event. Mobile must be a 10-digit Australian mobile (starts with <span className="font-mono">04</span>) — <span className="font-mono">+61</span> prefix accepted too.</p>
              <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="ec1-block">
                <Field label="Emergency contact #1 — name *">
                  <input required value={profile.ec1_name} onChange={(e) => setProfile((p) => ({ ...p, ec1_name: e.target.value }))} className="input" data-testid="input-ec1-name" />
                </Field>
                <Field label="Emergency contact #1 — mobile *">
                  <input required value={profile.ec1_mobile} onChange={(e) => setProfile((p) => ({ ...p, ec1_mobile: e.target.value }))} className="input" data-testid="input-ec1-mobile" placeholder="04XX XXX XXX" inputMode="tel" maxLength={20} />
                  {profile.ec1_mobile.trim() && !ec1MobileValid ? (
                    <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="ec1-mobile-error">Enter a 10-digit Australian mobile (starts with 04).</p>
                  ) : null}
                </Field>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="ec2-block">
                <Field label="Emergency contact #2 — name (optional)">
                  <input value={profile.ec2_name} onChange={(e) => setProfile((p) => ({ ...p, ec2_name: e.target.value }))} className="input" data-testid="input-ec2-name" />
                </Field>
                <Field label="Emergency contact #2 — mobile (optional)">
                  <input value={profile.ec2_mobile} onChange={(e) => setProfile((p) => ({ ...p, ec2_mobile: e.target.value }))} className="input" data-testid="input-ec2-mobile" placeholder="04XX XXX XXX" inputMode="tel" maxLength={20} />
                  {profile.ec2_mobile.trim() && !ec2MobileValid ? (
                    <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="ec2-mobile-error">Enter a 10-digit Australian mobile (starts with 04) or leave blank.</p>
                  ) : null}
                </Field>
              </div>
            </div>
          </div>

          <StepHeader n={2} id="step-2" title="Build your itinerary" sub="Add every date, event element and role you'd like to do. Keep adding until you're done." done={itinerary.length > 0} />

          {/* Slot picker */}
          <div id="slot-picker" className="border border-black/10 bg-[#F9F9FB] p-5">
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Add a role to your itinerary</div>

            {/* Choose mode: pick a role yourself OR ask organiser */}
            <div className="mt-3 inline-flex border border-black/15" data-testid="slot-mode-toggle">
              <button type="button" onClick={() => setCurrent((c) => ({ ...c, unallocated: false, session: "", role: "", unlock_code: "" }))}
                data-testid="slot-mode-pick"
                className={`px-3 py-2 text-xs font-semibold ${!current.unallocated ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}>
                Pick a role yourself
              </button>
              <button type="button" onClick={() => setCurrent((c) => ({ ...c, unallocated: true, session: "", role: "", unlock_code: "" }))}
                data-testid="slot-mode-organiser"
                className={`px-3 py-2 text-xs font-semibold ${current.unallocated ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>
                Get organiser to select me a role
              </button>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <Field label="Date">
                <select value={current.day} onChange={(e) => setCurrent((c) => ({ ...c, day: e.target.value, session: "", role: "", unlock_code: "" }))} className="input" data-testid="select-day">
                  <option value="">Choose a date</option>
                  {(ev.days || []).map((d) => <option key={d.date} value={d.date}>{formatAuDate(d.date)}</option>)}
                </select>
              </Field>
              {!current.unallocated ? (
                <>
                  <Field label="Event element">
                    <select disabled={!current.day} value={current.session} onChange={(e) => setCurrent((c) => ({ ...c, session: e.target.value, role: "" }))} className="input disabled:bg-zinc-100" data-testid="select-session">
                      <option value="">{current.day ? "Choose an event element" : "Choose a date first"}</option>
                      {daySessions.map((s) => {
                        const n = sessionName(s);
                        const start = s.start || "", end = s.end || "";
                        const range = start || end ? ` (${start || "?"} – ${end || "?"})` : "";
                        const loc = (s.location || "").trim();
                        const locPart = loc ? ` — 📍 ${loc}` : "";
                        return <option key={n} value={n}>{n}{range}{locPart}</option>;
                      })}
                    </select>
                  </Field>
                  <Field label={(
                    <span className="inline-flex items-center gap-1.5">
                      Role
                      {isLockedRoleSelected ? <Lock className="h-3 w-3 text-[#8B4513]" data-testid="role-lock-indicator" /> : null}
                      {selectedRoleDescription ? (
                        <span
                          className="relative inline-flex cursor-help"
                          data-testid="role-info-indicator"
                          title={selectedRoleDescription}
                        >
                          <Info className="h-3 w-3 text-[#002FA7]" />
                        </span>
                      ) : null}
                    </span>
                  )}>
                    <select disabled={!current.session} value={current.role} onChange={(e) => setCurrent((c) => ({ ...c, role: e.target.value, unlock_code: "" }))} className="input disabled:bg-zinc-100" data-testid="select-role" title={lockedRoles.length ? "Locked roles are marked 🔒 — an unlock code is required" : ""}>
                      <option value="">{current.session ? "Choose a role" : "Choose an event element first"}</option>
                      {sessionRoles.map((r) => {
                        const hasDesc = !!sessionRoleDescriptions[r];
                        const locked = lockedRoles.includes(r);
                        return (
                          <option key={r} value={r} title={locked ? "Role locked — code required" : undefined}>
                            {locked ? "🔒 " : ""}{r}{hasDesc ? "  ⓘ" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                </>
              ) : (
                <div className="md:col-span-2 border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-3 text-xs text-[#002FA7]" data-testid="unallocated-info-block">
                  <b>You're asking the organiser to pick a role for you on this day.</b> They'll allocate you to a suitable slot and email you to confirm. You'll appear in the organiser's "unallocated" list until they place you.
                </div>
              )}
            </div>
            {isLockedRoleSelected && !current.unallocated ? (
              <div className="mt-3 flex flex-wrap items-center gap-3 border border-[#8B4513]/40 bg-[#8B4513]/[0.06] p-3" data-testid="unlock-code-block">
                <Lock className="h-4 w-4 text-[#8B4513]" />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-[#8B4513]">This role is locked</div>
                  <div className="text-xs text-[#5A2E0C]">Ask the event organiser for the 3-digit unlock code for this role.</div>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{3}"
                  maxLength={3}
                  value={current.unlock_code}
                  onChange={(e) => setCurrent((c) => ({ ...c, unlock_code: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
                  className={`input max-w-[110px] text-center font-mono tracking-widest text-lg ${unlockCheckState === "bad" ? "border-red-500 bg-red-50" : unlockCheckState === "ok" ? "border-emerald-500 bg-emerald-50" : ""}`}
                  placeholder="•••"
                  data-testid="unlock-code-input"
                />
                <span className={`text-[11px] font-semibold ${unlockCheckState === "ok" ? "text-emerald-700" : unlockCheckState === "bad" ? "text-red-700" : "text-[#8B4513]"}`} data-testid="unlock-code-hint">
                  {unlockCheckState === "checking" ? "Checking…" : unlockCheckState === "ok" ? "✓ Correct code" : unlockCheckState === "bad" ? "✗ Wrong code" : "3 digits required"}
                </span>
              </div>
            ) : null}
            {!current.unallocated && selectedSession ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border border-[#002FA7]/40 bg-[#002FA7]/[0.05] p-3 text-sm" data-testid="session-info-panel">
                <div>
                  <span className="mr-1 text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Time</span>
                  <span className="font-mono font-semibold">
                    {(selectedSession.start || selectedSession.end) ? `${selectedSession.start || "?"} – ${selectedSession.end || "?"}` : "Not set"}
                  </span>
                </div>
                {(selectedSession.location || "").trim() ? (
                  <div>
                    <span className="mr-1 text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Location</span>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedSession.location.trim())}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="session-info-location-link"
                      title="Open in Google Maps"
                      className="font-semibold text-[#002FA7] underline decoration-dotted hover:decoration-solid"
                    >📍 {selectedSession.location.trim()}</a>
                  </div>
                ) : (
                  <div className="text-xs italic text-zinc-500" data-testid="session-info-location-empty">No location set — check with organiser.</div>
                )}
              </div>
            ) : null}
            {selectedRoleDescription ? (
              <div className="mt-3 flex items-start gap-3 border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-3" data-testid="role-description-panel">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#002FA7]" />
                <div className="text-xs text-black">
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">What "{current.role}" involves</div>
                  <div className="whitespace-pre-wrap leading-relaxed" data-testid="role-description-text">{selectedRoleDescription}</div>
                </div>
              </div>
            ) : (current.role ? (
              <div className="mt-3 text-[11px] italic text-zinc-500" data-testid="role-description-empty">No description provided for this role.</div>
            ) : null)}
            <div className="mt-4 flex justify-end">
              <button type="button" disabled={!canAddSlot} onClick={addSlot} data-testid="add-slot-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40">
                <Plus className="h-3.5 w-3.5" /> Add to itinerary
              </button>
            </div>
          </div>

          {/* Itinerary list */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Your itinerary ({itinerary.length})</div>
            {itinerary.length ? (
              <ul className="mt-3 space-y-2" data-testid="itinerary-list">
                {itinerary.map((s, i) => {
                  const d = daysMap[s.day];
                  const sess = s.unallocated ? null : d?.sessions?.find((x) => sessionName(x) === s.session);
                  const start = sess?.start || "";
                  const end = sess?.end || "";
                  const timeStr = (start || end) ? `${start || "?"} – ${end || "?"}` : "";
                  const locStr = (sess?.location || "").trim();
                  const mapsHref = locStr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locStr)}` : "";
                  const locked = !s.unallocated && (sess?.locked_roles || []).includes(s.role);
                  return (
                    <li key={`${s.day}-${s.session || "_any_"}-${s.role || "_any_"}-${i}`} data-testid={`itinerary-item-${i}`} className={`flex items-center gap-3 border ${s.unallocated ? "border-[#002FA7]/40 bg-[#002FA7]/[0.04]" : "border-black/15 bg-white"} px-4 py-3 text-sm`}>
                      <div className="flex-1">
                        <div className="font-semibold flex items-center gap-1.5">
                          {formatAuDate(s.day)}
                          {locked ? <Lock className="h-3 w-3 text-[#8B4513]" data-testid={`itinerary-lock-${i}`} /> : null}
                          {s.unallocated ? <span className="ml-1 rounded-sm bg-[#002FA7] px-1.5 py-0.5 text-[9px] font-medium text-white" data-testid={`itinerary-unallocated-badge-${i}`}>Organiser will pick</span> : null}
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-zinc-600" data-testid={`itinerary-slot-${i}`}>
                          {s.unallocated
                            ? (s.preferences && s.preferences.length
                                ? `Time conflict — organiser will pick one from your ${s.preferences.length} preferences`
                                : "Awaiting organiser allocation — a suitable role will be chosen for you")
                            : (
                              <>
                                {s.session}{timeStr ? ` · ${timeStr}` : ""}
                                {locStr ? (
                                  <>
                                    {" · "}
                                    <a
                                      href={mapsHref}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`itinerary-location-link-${i}`}
                                      className="text-[#002FA7] underline decoration-dotted hover:decoration-solid"
                                      title="Open in Google Maps"
                                    >📍 {locStr}</a>
                                  </>
                                ) : null}
                                {` · ${s.role}`}
                              </>
                            )}
                        </div>
                        {s.unallocated && s.preferences && s.preferences.length ? (
                          <ul className="mt-1.5 flex flex-wrap gap-1" data-testid={`itinerary-preferences-${i}`}>
                            {s.preferences.map((p, pi) => (
                              <li key={pi} className="border border-[#002FA7]/40 bg-white px-2 py-0.5 text-[10px] font-mono text-[#002FA7]">
                                {p.session} · {p.role}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <button onClick={() => removeSlot(i)} data-testid={`itinerary-remove-${i}`} className="inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-600 hover:text-white"><Trash2 className="h-3 w-3" /> Remove</button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-3 border border-dashed border-black/20 p-4 text-center text-xs italic text-zinc-500">Nothing added yet. Pick a date, event element and role above.</p>
            )}
          </div>

          {/* Nudge bar */}
          <div className="mt-6 border-y-2 border-[#002FA7] bg-[#002FA7] px-4 py-3 text-center" data-testid="add-more-nudge-bar">
            <div className="text-xs font-black uppercase tracking-widest text-white md:text-sm">
              Keep adding roles on different days until done — then go to Step 3 below.
            </div>
          </div>

          <StepHeader n={3} id="step-3" title={disclaimerUrl ? "Disclaimer & submit" : "Check & submit"} sub={disclaimerUrl ? "Read and sign the disclaimer, answer the human check, then submit." : "Answer the human check, then submit your itinerary."} done={!!canSubmit} />

          {/* Disclaimer + typed digital signature */}
          {disclaimerUrl ? (
            <div className="border border-amber-300 bg-amber-50 p-4" data-testid="disclaimer-block">
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-800" />
                <div className="flex-1">
                  <div className="font-display text-base font-black tracking-tight text-amber-900">Disclaimer</div>
                  <p className="mt-1 text-xs text-amber-900">You must open and read the organiser's disclaimer before you can tick the acknowledgement box.</p>
                  <a
                    href={disclaimerUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="view-disclaimer-btn"
                    onClick={() => setDisclaimerOpened(true)}
                    className="mt-2 inline-flex items-center gap-1.5 border border-amber-700 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-700 hover:text-white"
                  >
                    <ExternalLink className="h-3 w-3" /> {disclaimerOpened ? "PDF opened — you can re-open it" : "Open PDF (required before you can acknowledge)"}
                  </a>
                  {disclaimerOpened ? <p className="mt-1 text-[11px] font-semibold text-emerald-700" data-testid="disclaimer-opened-indicator">✓ PDF has been opened</p> : null}
                </div>
              </div>
              <label className={`mt-4 flex items-start gap-2 border-t border-amber-300 pt-3 text-sm ${disclaimerOpened ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                <input
                  type="checkbox"
                  checked={disclaimerAck}
                  disabled={!disclaimerOpened}
                  onChange={(e) => setDisclaimerAck(e.target.checked)}
                  data-testid="disclaimer-checkbox"
                  className="mt-0.5 h-4 w-4 accent-amber-700"
                />
                <span className="text-amber-900"><b>I have read and accept the disclaimer.</b> I understand any risks involved and consent to volunteer under the conditions set out in the document above.{!disclaimerOpened ? " (Open the PDF first to enable this checkbox.)" : ""}</span>
              </label>
              <div className="mt-4 border-t border-amber-300 pt-3">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-widest text-amber-800">Digital signature — type your full name to sign</label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className="input w-full font-signature text-xl italic"
                  placeholder={fullName ? fullName : "First fill in your first and last name above"}
                  data-testid="disclaimer-signature-input"
                  disabled={!fullName}
                />
                {fullName && signature.trim() ? (
                  signatureMatches ? (
                    <p className="mt-1 text-[11px] text-emerald-800" data-testid="signature-status-ok">Signature accepted — matches your full name.</p>
                  ) : (
                    <p className="mt-1 text-[11px] text-red-700" data-testid="signature-status-mismatch">Signature must exactly match your full name (first + last) entered above.</p>
                  )
                ) : (
                  <p className="mt-1 text-[11px] italic text-amber-800">By typing your name above, you are digitally signing that you have read and accepted the disclaimer.</p>
                )}
              </div>
            </div>
          ) : null}

          {/* Captcha + submit */}
          <div className="mt-8 pt-2">
            <input type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} data-testid="hp-website" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <div className="mb-6 border border-black/10 bg-[#F4F4F5] p-4">
              <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Human check</div>
              <div className="mt-1 flex items-center gap-3">
                <span className="font-display text-lg font-black tracking-tighter" data-testid="captcha-question">{captcha.question || "Loading…"}</span>
                <input required value={captcha.answer} onChange={(e) => setCaptcha((c) => ({ ...c, answer: e.target.value }))} className="input max-w-[120px]" placeholder="Your answer" data-testid="captcha-answer" />
                <button type="button" onClick={loadCaptcha} data-testid="captcha-refresh" className="text-xs text-zinc-600 underline">New question</button>
              </div>
            </div>
            {/* Missing-answers reminder — appears when the submit is disabled */}
            {!canSubmit ? (
              <div className="mb-4 border-2 border-[#FF3B30] bg-[#FF3B30]/[0.06] px-4 py-3" data-testid="missing-answers-banner">
                <div className="font-display text-sm font-black uppercase tracking-widest text-[#FF3B30]">Make sure you answer all questions</div>
                <ul className="mt-1 list-inside list-disc text-[11px] text-zinc-700" data-testid="missing-answers-list">
                  {!contact.first_name.trim() ? <li data-testid="missing-first-name">First name</li> : null}
                  {!contact.last_name.trim() ? <li data-testid="missing-last-name">Last name</li> : null}
                  {!contact.mobile.trim() ? <li data-testid="missing-mobile">Mobile phone</li> : null}
                  {!contact.email.trim() ? <li data-testid="missing-email">Email address</li> : null}
                  {!profile.location ? <li data-testid="missing-location">Location (Adelaide, Regional SA or Interstate)</li> : null}
                  {!profile.ec1_name.trim() ? <li data-testid="missing-ec1-name">Emergency contact #1 — name</li> : null}
                  {!profile.ec1_mobile.trim() ? <li data-testid="missing-ec1-mobile">Emergency contact #1 — mobile</li> : profile.ec1_mobile.trim() && !ec1MobileValid ? <li data-testid="invalid-ec1-mobile">Emergency contact #1 mobile — must be a 10-digit Australian mobile (04XX XXX XXX)</li> : null}
                  {ec2Provided && profile.ec2_mobile.trim() && !ec2MobileValid ? <li data-testid="invalid-ec2-mobile">Emergency contact #2 mobile — must be a 10-digit Australian mobile (04XX XXX XXX)</li> : null}
                  {itinerary.length === 0 ? <li data-testid="missing-itinerary">At least one role in your itinerary</li> : null}
                  {!captcha.answer.trim() ? <li data-testid="missing-captcha">Human check answer</li> : null}
                  {disclaimerRequired && !disclaimerOpened ? <li data-testid="missing-disclaimer-open">Open the disclaimer PDF</li> : null}
                  {disclaimerRequired && disclaimerOpened && !disclaimerAck ? <li data-testid="missing-disclaimer-ack">Tick the disclaimer acknowledgement box</li> : null}
                  {disclaimerRequired && disclaimerAck && !signatureMatches ? <li data-testid="missing-disclaimer-sign">Digital signature (type your full name exactly)</li> : null}
                </ul>
              </div>
            ) : null}
            <button type="button" onClick={submit} disabled={!canSubmit || submitting} data-testid="submit-registration-btn" className="brand-btn inline-flex w-full items-center justify-center gap-3 px-16 py-8 text-base md:text-lg font-semibold disabled:opacity-50 md:w-auto">
              {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : <ListPlus className="h-6 w-6" />}
              {submitting ? "Submitting…" : "I HAVE SELECTED ALL MY ROLES"}
            </button>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById("slot-picker");
                  if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                }}
                data-testid="add-more-roles-btn"
                className="inline-flex w-full items-center justify-center gap-2 border-2 border-[#FF3B30] bg-[#FF3B30] px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-[#FF3B30] md:w-auto"
              >
                <Plus className="h-4 w-4" /> I WANT TO ADD MORE ROLES
              </button>
            </div>
            {!itinerary.length ? <p className="mt-2 text-[11px] italic text-zinc-500">Add at least one slot before submitting.</p> : null}
            {disclaimerRequired && (!disclaimerAck || !signatureMatches) ? <p className="mt-2 text-[11px] italic text-amber-800" data-testid="disclaimer-warning">You must tick the disclaimer box AND type your full name as your signature before submitting.</p> : null}
          </div>
        </div>
      </div>
      <style>{`
        .input { width:100%; border:1px solid rgba(0,0,0,0.15); background:white; padding: 10px 12px; font-size: 14px; border-radius: 2px; outline: none; }
        .input:focus { border-color: #002FA7; box-shadow: 0 0 0 3px rgba(0,47,167,0.12); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</span>{children}</label>;
}

function ResultScreen({ result, slug, ev, onReset, onAcceptSuggestion, navigate }) {
  const anyAllocated = (result.allocated || []).length > 0;
  const anyMissed = (result.unavailable || []).length > 0;
  const anyUnallocated = (result.unallocated || []).length > 0;
  const onlyUnallocated = anyUnallocated && !anyAllocated && !anyMissed;
  const daysMap = {};
  (ev?.days || []).forEach((d) => { daysMap[d.date] = d; });
  const slotTime = (day, sName) => {
    const d = daysMap[day]; if (!d) return "";
    const s = (d.sessions || []).find((x) => (typeof x === "string" ? x : x.name) === sName);
    if (!s) return "";
    const st = s.start || "", en = s.end || "";
    return (st || en) ? `${st || "?"} – ${en || "?"}` : "";
  };
  // Look up the venue for a given day + session so we can show it next to the
  // time on every itinerary row on the success screen (matches the itinerary
  // email format).
  const slotLocation = (day, sName) => {
    const d = daysMap[day]; if (!d) return "";
    const s = (d.sessions || []).find((x) => (typeof x === "string" ? x : x.name) === sName);
    return ((s?.location || "").trim());
  };
  let title, subtitle, IconEl;
  if (anyAllocated) {
    title = "Your itinerary is in.";
    subtitle = "A confirmation email has been sent to you covering every role below.";
    IconEl = <CheckCircle2 className="h-12 w-12 text-emerald-600" />;
  } else if (onlyUnallocated) {
    title = "Organiser will be in touch.";
    subtitle = "You asked the organiser to pick a role for you — they'll email you a confirmation as soon as they've placed you. You're on the chase list.";
    IconEl = <CheckCircle2 className="h-12 w-12 text-[#002FA7]" />;
  } else {
    title = "We couldn't allocate you at this time.";
    // Pull the first reason from the response payload so registrants immediately see
    // WHY it didn't work. Every unavailable row now carries a plain-English reason
    // (set on the backend at /api/public/events/{slug}/register-itinerary).
    const firstReason = (result.unavailable || [])[0]?.reason
      || "The role you asked for is no longer available.";
    subtitle = `Reason: ${firstReason} Don't worry — we've added you to the organiser's follow-up list AND the unallocated pool with the exact role you originally asked for, so they can either give it to you if it opens up or find you a similar spot.`;
    IconEl = <XCircle className="h-12 w-12 text-[#FF3B30]" />;
  }
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <div className="border border-black/10 bg-white p-8 md:p-12 shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
          {IconEl}
          <h1 className="mt-4 font-display text-4xl font-black tracking-tighter" data-testid="registration-success-title">{title}</h1>
          <p className="mt-3 text-sm text-zinc-600" data-testid="registration-success-subtitle">{subtitle}</p>

          {anyUnallocated ? (
            <div className="mt-6" data-testid="unallocated-days-block">
              <div className="text-xs uppercase tracking-widest text-[#002FA7]">Awaiting organiser</div>
              <ul className="mt-2 space-y-2">
                {result.unallocated.map((u, i) => (
                  <li key={`unalloc-${u.day}-${i}`} data-testid={`unallocated-day-item-${i}`} className="border border-[#002FA7]/30 bg-[#002FA7]/[0.05] p-4 text-sm">
                    <div className="font-semibold">{formatAuDate(u.day)}</div>
                    <div className="mt-0.5 text-xs text-zinc-700">You've asked the organiser to pick a role for this day — they'll be in touch shortly.</div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {anyAllocated ? (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-emerald-700">Allocated · please confirm</div>
              <ul className="mt-2 space-y-2">
                {result.allocated.map((a, i) => {
                  const t = slotTime(a.day, a.session);
                  const loc = slotLocation(a.day, a.session);
                  const mapsHref = loc ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}` : "";
                  const rowKey = `${a.day}-${a.session}-${a.role}-${i}`;
                  return (
                    <li key={rowKey} data-testid={`allocated-item-${i}`} className="border border-emerald-200 bg-emerald-50 p-4 text-sm">
                      <div className="font-semibold">{formatAuDate(a.day)}</div>
                      <div className="mt-0.5 font-mono text-xs text-zinc-700" data-testid={`allocated-item-detail-${i}`}>
                        {a.session}{t ? ` · ${t}` : ""}
                        {loc ? (
                          <>
                            {" · "}
                            <a href={mapsHref} target="_blank" rel="noreferrer" data-testid={`allocated-item-location-${i}`} className="text-[#002FA7] underline decoration-dotted hover:decoration-solid" title="Open in Google Maps">📍 {loc}</a>
                          </>
                        ) : null}
                        {` · ${a.role}`}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {result.confirmation_link ? (
                <div className="mt-4 rounded-sm border border-blue-200 bg-blue-50 p-4 text-xs text-blue-900">
                  <div className="font-semibold">One-click confirmation link (for every role above)</div>
                  <a className="mt-1 block break-all underline" href={result.confirmation_link} data-testid="mock-confirmation-link">{result.confirmation_link}</a>
                </div>
              ) : null}
            </div>
          ) : null}

          {anyMissed ? (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[#FF3B30]">We couldn't allocate you at this time</div>
              <ul className="mt-2 space-y-3">
                {result.unavailable.map((u, i) => {
                  const tr = slotTime(u.requested.day, u.requested.session);
                  const lr = slotLocation(u.requested.day, u.requested.session);
                  const ts = u.suggestion ? slotTime(u.suggestion.day, u.suggestion.session) : "";
                  const ls = u.suggestion ? slotLocation(u.suggestion.day, u.suggestion.session) : "";
                  const rowKey = `${u.requested.day}-${u.requested.session}-${u.requested.role}-${i}`;
                  return (
                    <li key={rowKey} data-testid={`unavailable-item-${i}`} className="border border-red-200 bg-red-50 p-4 text-sm">
                      <div className="font-mono text-xs"><b>You asked for:</b> {formatAuDate(u.requested.day)} · {u.requested.session}{tr ? ` · ${tr}` : ""}{lr ? ` · 📍 ${lr}` : ""} · {u.requested.role}</div>
                      {u.reason ? (
                        <div className="mt-2 text-xs text-red-800" data-testid={`unavailable-reason-${i}`}><b>Why:</b> {u.reason}</div>
                      ) : null}
                      <div className="mt-2 text-[11px] italic text-zinc-600" data-testid={`unavailable-followup-note-${i}`}>Your original request has been logged and the organiser can see exactly what you asked for. You're on the chase list and the unallocated pool.</div>
                      {u.suggestion ? (
                        <>
                          <div className="mt-2 font-mono text-xs text-emerald-800"><b>Next best:</b> {formatAuDate(u.suggestion.day)} · {u.suggestion.session}{ts ? ` · ${ts}` : ""}{ls ? ` · 📍 ${ls}` : ""} · {u.suggestion.role}</div>
                          <button onClick={() => onAcceptSuggestion(u.suggestion)} data-testid={`accept-suggestion-btn-${i}`} className="mt-3 inline-flex items-center gap-1.5 border border-black bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-white hover:text-black">Book me for this instead</button>
                        </>
                      ) : (
                        <div className="mt-2 text-xs italic text-zinc-600">No alternate slot is free — a team member will contact you.</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={onReset} className="border border-black/20 px-6 py-3 text-sm font-medium hover:bg-black hover:text-white" data-testid="register-another-btn">Add more roles</button>
            <button onClick={() => navigate(`/events/${slug}`)} className="brand-btn px-6 py-3 text-sm font-medium">Back to event</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepHeader({ n, id, title, sub, done }) {
  return (
    <div id={id} className="mt-12 mb-4 flex scroll-mt-24 items-start gap-4 border-t-2 border-black pt-6 first-of-type:mt-8" data-testid={`step-header-${n}`}>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center font-display text-2xl font-black ${done ? "bg-emerald-600 text-white" : "bg-[#002FA7] text-white"}`}>
        {done ? <Check className="h-6 w-6" /> : n}
      </div>
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">Step {n} of 3</div>
        <h3 className="font-display text-2xl font-black tracking-tighter">{title}</h3>
        <p className="mt-0.5 text-xs text-zinc-600">{sub}</p>
      </div>
    </div>
  );
}

function StepProgress({ steps }) {
  const go = (n) => document.getElementById(`step-${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <nav className="sticky top-0 z-30 -mx-8 grid grid-cols-3 border-y border-black/10 bg-white/95 backdrop-blur md:-mx-12" data-testid="step-progress">
      {steps.map((s) => (
        <button key={s.n} type="button" onClick={() => go(s.n)} data-testid={`step-progress-${s.n}`}
          className={`flex items-center justify-center gap-2 border-b-4 px-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wider sm:text-xs ${s.done ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-600 hover:text-black"}`}>
          <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center text-xs font-black ${s.done ? "bg-emerald-600 text-white" : "bg-black text-white"}`}>{s.done ? <Check className="h-3.5 w-3.5" /> : s.n}</span>
          <span className="hidden sm:inline">{s.label}</span>
          <span className="sm:hidden">Step {s.n}</span>
        </button>
      ))}
    </nav>
  );
}
